import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BookOpen,
  Eye,
  FileText,
  MoreVertical,
  Pencil,
  Tag,
  Trash2,
  Activity,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type CustomerRowActionId =
  | 'view'
  | 'ledger'
  | 'register'
  | 'payment'
  | 'invoices'
  | 'quotations'
  | 'specialPrices'
  | 'edit'
  | 'delete';

type MenuItem = {
  id: CustomerRowActionId;
  label: string;
  icon: typeof Pencil;
  onClick: () => void;
  danger?: boolean;
  hidden?: boolean;
};

interface CustomerRowActionsMenuProps {
  items: MenuItem[];
}

export default function CustomerRowActionsMenu({ items }: CustomerRowActionsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const visible = items.filter((i) => !i.hidden);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const placeMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuWidth = 200;
    const approxHeight = Math.min(360, visible.length * 36 + 16);
    let left = rect.right - menuWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    let top = rect.bottom + 4;
    if (top + approxHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - approxHeight - 4);
    }
    setPos({ top, left });
  };

  const toggle = () => {
    if (!open) placeMenu();
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        title={t('common.actions', 'Actions')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: pos.top, left: pos.left }}
            className="fixed z-[80] w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm"
          >
            {visible.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    item.onClick();
                  }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 ${
                    item.danger ? 'text-red-700' : 'text-gray-700'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${item.danger ? 'text-red-500' : 'text-gray-500'}`} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

export const CustomerRowActionIcons = {
  BookOpen,
  Eye,
  FileText,
  Pencil,
  Tag,
  Trash2,
  Activity,
};
